// F13a (FR-O10/O10a, FR-AD7): operator quota overrides, the public-delivery kill switch and
// user suspend. Every action asks for a reason (ops audit trail).
// PUT /ops/orgs/{id}/quota, POST /ops/egress/org/{id}/hard-stop|release,
// POST /ops/users/{id}/suspend|unsuspend. Built from @grackle/ui (SD35) and DS tokens only.
// The limit fields are loaded from GET /ops/orgs/{id}/quota because PUT replaces the whole
// override: blank means "use the default", so unseen fields must not be cleared by accident.
import { useEffect, useState } from "react";
import { Card } from "@grackle/ui";
import ConfirmPrompt from "../components/ConfirmPrompt";
import { api, ApiFail } from "../api/client";
import { Button, Field, Notice } from "../components/ui";

type Pending =
  | { kind: "quota" }
  | { kind: "hard-stop" }
  | { kind: "release" }
  | { kind: "suspend"; userId: string; label: string }
  | { kind: "unsuspend"; userId: string; label: string };

const GB = 1024 ** 3;

interface OrgQuotaOut {
  max_storage_bytes: number | null;
  max_recording_seconds: number | null;
  max_members: number | null;
  max_egress_bytes_30d: number | null;
  max_hosted_stt_seconds_30d: number | null;
}

function show(n: number | null, scale = 1): string {
  return n == null ? "" : String(n / scale);
}

function toNumber(v: string, scale = 1): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * scale) : null;
}

export default function OpsQuotaPanel({
  orgId,
  members,
}: {
  orgId: string;
  members: { user_id: string; name: string; email: string }[];
}) {
  const [storageGb, setStorageGb] = useState("");
  const [maxMembers, setMaxMembers] = useState("");
  const [egressGb, setEgressGb] = useState("");
  const [recordingMinutes, setRecordingMinutes] = useState("");
  const [sttMinutes, setSttMinutes] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [message, setMessage] = useState<{ tone: "error" | "info"; text: string } | null>(null);

  useEffect(() => {
    api<OrgQuotaOut>("GET", `/ops/orgs/${orgId}/quota`)
      .then((q) => {
        setStorageGb(show(q.max_storage_bytes, GB));
        setMaxMembers(show(q.max_members));
        setEgressGb(show(q.max_egress_bytes_30d, GB));
        setRecordingMinutes(show(q.max_recording_seconds, 60));
        setSttMinutes(show(q.max_hosted_stt_seconds_30d, 60));
      })
      .catch((err) => setMessage({ tone: "error", text: err instanceof ApiFail ? err.message : "Couldn't load the quota." }));
  }, [orgId]);

  async function run(reason: string) {
    const p = pending;
    setPending(null);
    if (!p) return;
    try {
      if (p.kind === "quota") {
        await api("PUT", `/ops/orgs/${orgId}/quota`, {
          reason,
          max_storage_bytes: toNumber(storageGb, GB),
          max_members: toNumber(maxMembers),
          max_egress_bytes_30d: toNumber(egressGb, GB),
          max_recording_seconds: toNumber(recordingMinutes, 60),
          max_hosted_stt_seconds_30d: toNumber(sttMinutes, 60),
        });
        setMessage({ tone: "info", text: "Quota saved. It applies from the next request." });
      } else if (p.kind === "hard-stop" || p.kind === "release") {
        await api("POST", `/ops/egress/org/${orgId}/${p.kind}`, { reason });
        setMessage({
          tone: "info",
          text: p.kind === "hard-stop" ? "Public delivery stopped for this org." : "Public delivery released.",
        });
      } else {
        await api("POST", `/ops/users/${p.userId}/${p.kind}`, { reason });
        setMessage({
          tone: "info",
          text: p.kind === "suspend" ? `${p.label} suspended; sessions and tokens revoked.` : `${p.label} unsuspended.`,
        });
      }
    } catch (err) {
      setMessage({ tone: "error", text: err instanceof ApiFail ? err.message : "That action failed." });
    }
  }

  const title =
    pending?.kind === "quota"
      ? "Override quota"
      : pending?.kind === "hard-stop"
        ? "Stop public delivery"
        : pending?.kind === "release"
          ? "Release public delivery"
          : pending?.kind === "suspend"
            ? `Suspend ${pending.label}`
            : pending?.kind === "unsuspend"
              ? `Unsuspend ${pending.label}`
              : "";

  return (
    <Card size="sm" title="Quotas and public bandwidth" className="ops-quota" aria-label="Quotas and caps">
      {message ? <Notice tone={message.tone === "error" ? "error" : undefined}>{message.text}</Notice> : null}
      <p className="gk-sm">Leave a limit blank to use the platform default. Saving replaces the whole override.</p>
      <div className="ops-quota__row">
        <Field label="Storage (GB)" value={storageGb} onChange={(e) => setStorageGb(e.target.value)} inputMode="decimal" />
        <Field label="Members" value={maxMembers} onChange={(e) => setMaxMembers(e.target.value)} inputMode="numeric" />
        <Field
          label="Public bandwidth, 30 days (GB)"
          value={egressGb}
          onChange={(e) => setEgressGb(e.target.value)}
          inputMode="decimal"
        />
        <Field
          label="Longest recording (min)"
          value={recordingMinutes}
          onChange={(e) => setRecordingMinutes(e.target.value)}
          inputMode="decimal"
        />
        <Field
          label="Hosted AI, 30 days (min)"
          value={sttMinutes}
          onChange={(e) => setSttMinutes(e.target.value)}
          inputMode="decimal"
        />
      </div>
      <div className="ops-quota__row">
        <Button variant="primary" onClick={() => setPending({ kind: "quota" })}>
          Save quota
        </Button>
        <Button variant="danger" onClick={() => setPending({ kind: "hard-stop" })}>
          Stop public delivery
        </Button>
        <Button variant="secondary" onClick={() => setPending({ kind: "release" })}>
          Release
        </Button>
      </div>
      <Card size="flush" className="admin-table-card">
<table className="admin-table">
        <thead>
          <tr>
            <th className="gk-overline">Member</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const label = m.name || m.email;
            return (
              <tr key={m.user_id}>
                <td>
                  <div className="admin-row-name">{label}</div>
                  <div className="admin-row-sub">{m.email}</div>
                </td>
                <td>
                  <div className="row-actions">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setPending({ kind: "suspend", userId: m.user_id, label })}
                    >
                      Suspend user
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setPending({ kind: "unsuspend", userId: m.user_id, label })}
                    >
                      Unsuspend
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
</Card>
      {pending ? (
        <ConfirmPrompt
          open={true}
          title={title}
          message="Enter a reason. It is recorded in the ops audit trail."
          placeholder="e.g., abuse report #123, plan change…"
          onClose={() => setPending(null)}
          onConfirm={(reason) => void run(reason)}
        />
      ) : null}
    </Card>
  );
}
